/**
 * Reads the credential out of a browser URL. There are two shapes, and the first one is the point:
 *
 *   `{WebappBaseUrl}/{code}`                 — what this service mints now
 *   `{WebappBaseUrl}/v2/scan?token={code}`   — what is already sitting in patients' SMS inboxes
 *
 * **The short code IS the link** (docs/decisions.md D-14, D-41). The credential was already the
 * whole of the eight characters after `?token=`; `/v2/scan?token=` was fourteen further characters
 * of scheme-and-route in front of it, in a message billed by the character and wrapped by the
 * receiving handset. `https://scan.dev.us.predixa.health/GB7GMEGK` is a URL a patient can read, a
 * support agent can dictate, and an SMS gateway will not fold in half.
 *
 * This is the one place a browser URL becomes the values the rest of the app treats as
 * configuration, so a malformed or missing credential fails here rather than surfacing as a
 * confusing 401 three screens later.
 *
 * The link used to carry `&env={env}` outside production, "because the webapp is shared across
 * environments". It is not: every installation deploys its own webapp, at its own hostname, reading
 * its own `/config.json` — so the parameter named the environment the page was already served from,
 * and this module parsed it and handed it to nobody. It is gone from both ends
 * (docs/decisions.md D-13).
 */

/**
 * The shape a single path segment has to have before this app will read it as a grant code.
 *
 * **Why a shape test at all.** Under `/v2/scan?token=…` an absent or junk credential was
 * unmistakable: the route was static, so anything else on this origin was somebody else's problem.
 * Now every path is a candidate credential, and the ones that arrive are not all patients — a
 * crawler asks for `/robots.txt`, a browser asks for `/favicon.ico` unprompted, and a patient who
 * mistypes a URL asks for whatever they typed. Handing those to the scan flow would open a
 * challenge against Auth for each one and show a dead-link screen for a link that never existed.
 *
 * **The rule: exactly one path segment, ASCII letters and digits only.** A `/` fails it (a `$code`
 * param cannot contain one anyway, so the router settles that half — this only has to be honest
 * about it), and so does a `.`, a `-`, a `_` and anything percent-encoded.
 *
 * **Why that is safe against the real objects on this origin, in both directions.** Everything else
 * this distribution serves is either a FILE — `/config.json`, `/predixa-logo.png`, `/favicon.ico`,
 * `/robots.txt` — or NESTED — `/assets/*`, `/sdk/*` (tens of megabytes of vendored SDK), and
 * `/v2/scan` itself. A minted code is neither: no extension, one segment. So no code can shadow an
 * object, because no code contains a `.`; and no object can be read as a code, because every object
 * here carries an extension or a `/`. The two spaces are disjoint by construction rather than by a
 * list of exceptions that has to be maintained — and the one thing that maintains it is the rule
 * **nothing extensionless may be added to `public/` at the root**. Anything with an extension is
 * free.
 *
 * **Why this is NOT the code's own alphabet.** A generated code is eight characters of
 * `0123456789ABCDEFGHJKMNPQRSTVWXYZ` — thirty-two symbols, no I, L, O or U — and matching that
 * exactly would be wrong twice over. `LinkGrantShortCode.Normalize` deliberately admits every ASCII
 * letter and digit at any length, so that a six-digit code minted before laennec-libraries 1.9.0 is
 * still in somebody's phone and still resolves, and so that a patient who typed an O where the code
 * has a zero meets a FAILED LOOKUP rather than a closed door (docs/decisions.md D-14). Narrowing
 * here would convert that failed lookup into "Page not found", which is a worse answer to the same
 * mistake, and would break silently the day the library's `Length` changes.
 *
 * **Why the bounds are loose.** The two ways of being wrong are not symmetric. Too loose and a
 * crawler's `/health` costs one rate-limited challenge and shows a dead-link screen for something
 * that was never a link. Too tight and a real patient's real code shows "Page not found" and the
 * scan cannot happen at all. So the lower bound is 6 — the shortest code this platform ever minted —
 * which is enough to keep `/en`, `/api` and `/v2` out, and the upper bound is 32, which is room the
 * library would have to grow a very long way into before a real credential fell outside it.
 */
const SCAN_CODE = /^[0-9A-Za-z]{6,32}$/

/**
 * The credential carried by a bare single-segment path, or null when that path is not a credential
 * at all.
 *
 * Returned VERBATIM rather than upper-cased or stripped. Normalization is Auth's
 * (`LinkGrantShortCode.Normalize` runs at both ends of the reverse index, which is exactly why it
 * lives there and not in each caller), and a second normalizer here would be a second opinion about
 * what a credential is — the class of bug D-14's own remarks are about.
 */
export function parseScanCode(segment: string | undefined | null): string | null {
  if (typeof segment !== 'string') {
    return null
  }
  return SCAN_CODE.test(segment) ? segment : null
}

/** The legacy link's query string, as a shape. */
export interface ScanLink {
  readonly token: string
}

/**
 * Reads `?token=` off the OLD link shape.
 *
 * Kept, and kept admitting more than {@link parseScanCode} does, because the links already sent do
 * not un-send themselves and a scan grant lives eight hours. It is also the only shape that can
 * carry the seventy-four-character `base64url(grantId).base64url(signature)` form from before D-14:
 * that value contains a `.`, so it cannot be expressed as a bare path at all — which is the reason
 * the legacy route RESOLVES rather than redirecting to the new one (see router.tsx).
 */
export function parseScanLink(search: string): ScanLink | null {
  const params = new URLSearchParams(search)
  const token = params.get('token')
  if (!token || token.trim() === '') {
    return null
  }
  return { token }
}

/** What the router's `validateSearch` hands the page. `undefined` rather than `null` because that
 * is what TanStack Router's search-param types use for "absent". */
export interface ScanSearch {
  readonly token: string | undefined
}

/**
 * The router's own `validateSearch`, expressed through {@link parseScanLink} so the function the
 * tests describe is the function production runs.
 *
 * It used to be a separate `typeof search.token === 'string'` in router.tsx, and the two disagreed
 * on exactly one input: a whitespace-only token passed the router's test, reached the scan flow,
 * failed the challenge and showed the patient a dead-link screen three screens later - which is
 * precisely what the comment at the top of this file says must not happen.
 *
 * The `typeof` test is kept, not replaced. parseScanLink takes a string and the router hands over
 * whatever was in the URL, so `?token[]=a&token[]=b` arrives as something that is not one - the case
 * a bare typeof check was right about.
 */
export function validateScanSearch(search: Record<string, unknown>): ScanSearch {
  const query = new URLSearchParams()
  if (typeof search.token === 'string') {
    query.set('token', search.token)
  }

  const link = parseScanLink(query.toString())

  return { token: link?.token }
}
